import type { DescuentoDeVenta, LineaMostrador } from '@tanteo/compartido';
import type { PagoDeVenta } from './api';

/**
 * La venta que el cajero está armando, en `localStorage` para sobrevivir al refresco; es del
 * negocio y del día. `idVenta` viaja con el borrador y es el `uuid_cliente` idempotente de
 * `registrar_venta`: cerrar dos veces devuelve la que ya existe, no una segunda.
 */

const CLAVE = 'tanteo-venta-en-curso';

interface ClienteDeVenta {
  id: string;
  nombre: string;
  numeroDocumento: string;
}

export interface VentaEnCurso {
  idVenta: string;
  lineas: LineaMostrador[];
  cliente: ClienteDeVenta | null;
  /** La cuenta va por id: un retrato de la cuenta queda viejo si la renombran o la borran. */
  cuentaId: string | null;
  /** El reparto de un cobro mixto, si ya se hizo. */
  pagos: PagoDeVenta[] | null;
  /** El descuento a la venta completa, si se pidió. */
  descuento?: DescuentoDeVenta | null;
}

interface Guardado extends VentaEnCurso {
  negocioId: string;
  fecha: string;
}

/**
 * El reparto guardado, si cada pago trae su cuenta y un monto mayor que cero; si no, `null`: la venta
 * sigue y el cajero vuelve a repartir, en vez de que el servidor la rechace al cobrar.
 */
function repartoValidoGuardado(pagos: unknown): PagoDeVenta[] | null {
  if (!Array.isArray(pagos) || pagos.length === 0) return null;
  const bien = pagos.every(
    (p) => typeof p?.cuentaId === 'string' && p.cuentaId !== '' && Number.isFinite(p.monto) && p.monto > 0
  );
  return bien ? (pagos as PagoDeVenta[]) : null;
}

/** Una venta guardada que se entiende y tiene líneas; null si no. La usan el borrador y los pedidos. */
export function validarVenta(g: Partial<VentaEnCurso> | null | undefined): VentaEnCurso | null {
  if (typeof g?.idVenta !== 'string' || !g.idVenta) return null;
  if (!Array.isArray(g.lineas) || g.lineas.length === 0) return null;
  if (!g.lineas.every((l) => l?.sku && l?.productoId && Number(l.cantidad) > 0)) return null;
  return {
    idVenta: g.idVenta,
    // Un descuento guardado sin su id no lo acepta el servidor: la línea sigue, sin él.
    lineas: g.lineas.map((l) => (l.descuento && !l.descuento.descuentoId ? { ...l, descuento: null } : l)),
    cliente: g.cliente ?? null,
    cuentaId: g.cuentaId ?? null,
    pagos: repartoValidoGuardado(g.pagos),
    descuento: g.descuento?.descuentoId ? g.descuento : null,
  };
}

/** Interpreta lo guardado: null si no hay, si es de otro negocio u otro día, o si no se entiende. */
export function interpretarVenta(texto: string | null, negocioId: string, hoy: string): VentaEnCurso | null {
  if (!texto) return null;
  let g: Guardado;
  try {
    g = JSON.parse(texto) as Guardado;
  } catch {
    return null;
  }
  if (g?.negocioId !== negocioId || g?.fecha !== hoy) return null;
  return validarVenta(g);
}

export function guardarVenta(negocioId: string, fecha: string, venta: VentaEnCurso): void {
  const g: Guardado = { ...venta, negocioId, fecha };
  try {
    localStorage.setItem(CLAVE, JSON.stringify(g));
  } catch {
    /* almacenamiento lleno o bloqueado: la venta sigue en pantalla, solo no se podrá recuperar */
  }
}

export function leerVenta(negocioId: string, hoy: string): VentaEnCurso | null {
  try {
    return interpretarVenta(localStorage.getItem(CLAVE), negocioId, hoy);
  } catch {
    return null;
  }
}

export function olvidarVenta(): void {
  try {
    localStorage.removeItem(CLAVE);
  } catch {
    /* nada que hacer: la venta ya se cerró o se limpió */
  }
}
